import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "utils";

import { AlertBanner, Flex, ProgressBar, Typography } from "components";

const PROGRESS_COLORS = {
  INCOMPLETE: "#4d85f5",
  COMPLETE: "#065c45",
  EXCEEDED: "#db3804",
};

interface ProgressAnimatedProps {
  className?: string;
  currentValue?: number;
  title?: string;
  description?: string;
  onAnimationFinish?: () => void;
  showPercentValue?: boolean;
  showRemainingPercentage?: boolean;
  animationDuration?: number;
  strokeColor?: string;
  size?: number;
  errorMessage?: string;
  successMessage?: string;
  infoMessage?: string;
}

export const ProgressBarCardWithContent = ({
  className = "",
  currentValue = 0,
  title = "",
  description = "",
  onAnimationFinish = () => {},
  showPercentValue = true,
  showRemainingPercentage = true,
  animationDuration = 1000,
  strokeColor,
  size = 16,
  errorMessage = "",
  successMessage = "",
  infoMessage = "",
}: ProgressAnimatedProps) => {
  const { t } = useTranslation(["components"]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [displayedValue, setDisplayedValue] = useState(currentValue);
  const previousValueRef = useRef(currentValue);

  useEffect(() => {
    if (previousValueRef.current === currentValue) {
      return;
    }

    previousValueRef.current = currentValue;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (currentValue === 0) {
      setDisplayedValue(0);
      onAnimationFinish();
      return;
    }

    const stepTime = animationDuration / currentValue;

    const id = setInterval(() => {
      setDisplayedValue((val) => {
        const nextVal = val + 1;
        if (nextVal >= currentValue) {
          clearInterval(id);
          return currentValue;
        }
        return nextVal;
      });
    }, stepTime);

    intervalRef.current = id;

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue, animationDuration]);

  useEffect(() => {
    if (displayedValue === currentValue && currentValue !== 0) {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
      onAnimationFinish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedValue]);

  const getStrokeColor = () => {
    if (strokeColor) return strokeColor;

    if (displayedValue < 100) {
      return PROGRESS_COLORS.INCOMPLETE;
    }
    if (displayedValue === 100) {
      return PROGRESS_COLORS.COMPLETE;
    }
    return PROGRESS_COLORS.EXCEEDED;
  };

  const getRemainingPercentage = () => {
    const remaining = 100 - displayedValue;
    return `${remaining}% ${t("progressBarCardWithContent.remaining")}`;
  };

  const getExceededPercentage = () => {
    const exceeded = Math.round(currentValue - 100);
    return `${exceeded}% ${t("progressBarCardWithContent.overLimit")}`;
  };

  const showError = displayedValue > 100;
  const showSuccess = displayedValue === 100;
  const showInfo = displayedValue < 100 && displayedValue >= 0;

  return (
    <div>
      <Flex vertical className={`padding-100 progress-bar-card-with-context ${className}`}>
        <Flex justify="space-between" className="margin-bottom-50">
          {title && (
            <Typography variant="paragraph-m" bold>
              {title}
            </Typography>
          )}
          {showPercentValue && <Typography variant="paragraph-m" bold>{`${Math.round(currentValue)}%`}</Typography>}
        </Flex>

        <ProgressBar
          percent={Math.round(displayedValue > 100 ? 100 : displayedValue)}
          type="line"
          showInfo={false}
          strokeColor={getStrokeColor()}
          size={["100%", size]}
        />

        <Flex vertical justify="space-between" className="margin-top-50">
          <Flex>
            {showRemainingPercentage && displayedValue < 100 && (
              <Typography variant="paragraph-m" color="grey-600">
                {getRemainingPercentage()}
              </Typography>
            )}
          </Flex>
          <Flex>
            {showSuccess && <Typography variant="paragraph-m">{successMessage}</Typography>}
            {showError && (
              <Typography variant="paragraph-m" color="red-500">
                {getExceededPercentage()}
              </Typography>
            )}
            {description && <Typography variant="paragraph-m">{description}</Typography>}
          </Flex>
        </Flex>

        {showError && <AlertBanner className="margin-top-200" type="error" description={errorMessage} />}
        {showInfo && infoMessage && <AlertBanner className="margin-top-100" type="info" description={infoMessage} />}
      </Flex>
    </div>
  );
};
